import { ChartConfig, ChartContainer, Skeleton } from '@bpinternal/ui-kit-next'
import { Line, LineChart } from 'recharts'
import { BotAnalyticsMetric } from '~/features/bots/types'
import { useAnalyticsMetric } from '~/features/home/hooks/useAnalyticsMetric'
import { cn, formatters } from '~/utils'
import { useAgentAnalyticsContext } from './AgentAnalyticsContext'

type AnalyticsOverviewProps = {
  botId: string
  workspaceId: string
  metric: BotAnalyticsMetric
  className?: string
}

export const AnalyticsOverviewSkeleton = () => (
  <div className="grid-rows-auto grid grid-cols-2 justify-items-start gap-4 rounded-lg border p-4 md:rounded-none md:border-0">
    <Skeleton className="col-span-2 h-6 w-full" />
    <Skeleton className="size-8" />
    <Skeleton className="col-start-2 row-span-2 row-start-2 h-16 w-full md:row-span-1 md:h-8" />
    <Skeleton className="h-4 w-full" />
  </div>
)

export const AnalyticsOverview = ({ botId, workspaceId, metric, className }: AnalyticsOverviewProps) => {
  const { dateShortcut, getLabel } = useAgentAnalyticsContext()
  const metricLabel = getLabel(metric)

  const { analyticsDataValues, deltaFormatted, chartData } = useAnalyticsMetric(botId, workspaceId, {
    extractValue: (record) => {
      if (record && typeof record === 'object' && metric in record) {
        return typeof record[metric] === 'number' ? record[metric] : 0
      }
      return 0
    },
    dateShortcut,
    chartDataKey: metricLabel,
    valueKey: 'analyticsDataValues',
  })

  const chartConfig: ChartConfig = {
    [metricLabel]: {
      color: `hsl(var(--foreground))`,
    },
  }

  return (
    <div
      className={cn(
        'grid-rows-auto grid grid-cols-2 justify-items-start gap-4 rounded-lg border p-4 md:rounded-none md:border-0',
        className
      )}
    >
      <h4 className="col-span-2 text-nowrap text-sm font-medium">{metricLabel}</h4>
      <p className="truncate text-2xl font-bold">{formatters.numberLocale(analyticsDataValues, 0, 'fr-FR')}</p>
      <ChartContainer
        config={chartConfig}
        className="col-start-2 row-span-2 row-start-2 h-16 w-full duration-300 md:row-span-1 md:h-8"
      >
        <LineChart accessibilityLayer data={chartData}>
          <Line
            dataKey={metricLabel}
            type="monotone"
            stroke={chartConfig[metricLabel]?.color ?? 'hsl(var(--foreground))'}
            strokeWidth={1}
            dot={false}
          />
        </LineChart>
      </ChartContainer>
      <p className="text-xs text-muted-foreground md:col-span-2">{deltaFormatted}</p>
    </div>
  )
}
